import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { BookCallButton } from '../components/BookCallButton';
export function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const credibility = ['Former product leader at NBC, Yahoo, Evite, and Friendster', 'Hiring manager and team leader across consumer web, mobile, social, and AI', 'Designed and piloted an AI-assisted career coaching program at ASU', 'Training through Youth Coaching Institute and ScholarMatch', 'Mentored new grads and emerging talent', 'Helped young people build confidence, direction, and real-world readiness'];
  return <article className="py-12 md:py-20 px-6 md:px-12 max-w-3xl mx-auto" data-id="element-210">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-teal transition-colors mb-12" data-id="element-211">
        <ArrowLeftIcon className="w-4 h-4" data-id="element-212" />
        <span data-id="element-213">Back to home</span>
      </Link>

      <p className="text-xs uppercase tracking-widest text-accent mb-4 font-medium" data-id="element-214">
        Why I started Year One
      </p>
      <h1 className="text-5xl md:text-6xl font-serif leading-[1.05] mb-12" data-id="element-215">
        One conversation can change everything
        <span className="text-accent" data-id="element-216">.</span>
      </h1>

      <div className="flex items-center gap-5 mb-12 pb-12 border-b border-ink/10" data-id="element-217">
        <img src="https://cdn.magicpatterns.com/uploads/rHY1f3YTu8xTAzKyKufzyh/profile-larissa.jpg" alt="Larissa, founder of Year One Mentor" className="w-16 h-16 rounded-full object-cover" data-id="element-218" />
        <div data-id="element-219">
          <p className="font-serif text-xl text-ink" data-id="element-220">Larissa</p>
          <p className="text-sm text-ink/60" data-id="element-221">Founder, Year One Mentor</p>
        </div>
      </div>

      <div className="space-y-6 text-lg text-ink/80 leading-relaxed font-light" data-id="element-222">
        <p className="font-serif text-2xl md:text-3xl text-ink italic leading-snug" data-id="element-223">
          I was the underdog from the start.
        </p>
        <p data-id="element-224">
          I flunked my first year of college and was told I would probably end
          up working at McDonald's. I was not the obvious success story. I had
          to figure things out the hard way.
        </p>
        <p data-id="element-225">
          While working retail, I met a customer who worked at NBC. I got
          curious about her job, she offered me a tour of the lot, and I
          actually followed up.
        </p>
        <p className="font-serif text-xl md:text-2xl text-teal leading-snug py-2" data-id="element-226">
          That relationship led to my first opportunity in tech.
        </p>
        <p data-id="element-227">
          What started as a way to help pay for college ended up launching my
          career. I went from selling shoes to working in Silicon Valley during
          the dot-com boom alongside founders, executives, and some of the top
          minds in tech.
        </p>
        <p data-id="element-228">
          Over the years, I helped build products across consumer web, mobile,
          social, and now AI at companies like NBC, Yahoo, Friendster, and
          Evite.
        </p>
        <p data-id="element-229">
          I was often the square peg in the room. A creative in a technical
          world. I did not come from the traditional Silicon Valley pipeline or
          pedigree. What carried me was grit, curiosity, empathy, and the
          ability to connect with people.
        </p>
        <p data-id="element-230">
          Looking back, I realize my career was shaped as much by relationships,
          mentorship, and people who believed in me as it was by talent or
          skill.
        </p>
        <p data-id="element-231">
          As I stepped away from big tech and became a mother, my definition of
          success began to change. I became more interested in meaningful work,
          mentorship, and helping others find their way.
        </p>
        <p data-id="element-232">
          That led me to Arizona State University, where I helped develop and
          pilot an AI-assisted career exploration and coaching program designed
          to support students and recent graduates navigating an increasingly
          uncertain job market.
        </p>
        <p data-id="element-233">
          I saw the same thing over and over again: smart, capable young people
          with degrees, but no roadmap. They were overwhelmed by what they saw
          on LinkedIn, hearing conflicting advice, collecting credentials, and
          still unsure how to actually build a career or define success for
          themselves.
        </p>
        <p data-id="element-234">
          Career services often teach students how to apply for jobs. What many
          grads really need is confidence, personalized guidance, real-world
          perspective, and support from someone who has actually built teams,
          hired people, and navigated the industry firsthand.
        </p>
        <p className="font-serif text-2xl md:text-3xl text-ink leading-snug py-2" data-id="element-235">
          That is why I started Year One Mentor
          <span className="text-accent" data-id="element-236">.</span>
        </p>
        <p data-id="element-237">
          Year One Mentor is not just resume help or job search coaching. It is
          designed to help emerging talent build the foundation they need to
          launch a meaningful career and life.
        </p>
        <p data-id="element-238">
          I help grads figure out where they fit, how to tell their story, how
          to build relationships, gain experience, and develop the confidence
          and resilience needed to navigate the real world.
        </p>
        <p data-id="element-239">
          Because sometimes one conversation, one relationship, or one person
          believing in you can change your entire trajectory.
        </p>
        <p className="font-serif text-xl md:text-2xl text-teal italic leading-snug pt-2" data-id="element-240">
          I know. It changed mine.
        </p>
      </div>

      <div className="mt-16 bg-white p-8 rounded-2xl border border-teal/10 shadow-sm" data-id="element-241">
        <h3 className="text-xl font-serif mb-4 text-teal" data-id="element-242">
          Experience & Credibility
        </h3>
        <ul className="space-y-3" data-id="element-243">
          {credibility.map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-ink/70" data-id="element-244">
              <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" data-id="element-245"></div>
              <span data-id="element-246">{item}</span>
            </li>)}
        </ul>
      </div>

      <div className="mt-12 text-center" data-id="element-247">
        <BookCallButton className="inline-flex items-center justify-center px-6 py-3 bg-teal text-white font-medium rounded-md hover:bg-teal/90 transition-colors duration-200" data-id="element-248" />
      </div>
    </article>;
}